import { useState, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, AlertTriangle } from "lucide-react";

// Admite coma o punto como separador decimal y descarta cualquier carácter que no
// sea dígito, quedándose solo con el primer separador para evitar un NaN si el
// usuario teclea algo como "2.8.5".
function sanitizeDecimal(raw) {
  let v = raw.replace(/,/g, ".").replace(/[^0-9.]/g, "");
  const firstDot = v.indexOf(".");
  if (firstDot !== -1) {
    v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
  }
  return v;
}

// Selector repetible de residuos: permite añadir N pares {tipo, cantidad} a partir
// de un catálogo (RESIDUO_CATALOG del backend). Usado tanto en el check-in ciudadano
// como en la confirmación de entrada y el registro de salida en backoffice.
//
// Expone getPendingEntry() por ref: si el usuario escribió tipo+cantidad pero no
// pulsó "Añadir", el formulario padre puede leer esa entrada pendiente (ya
// recortada al máximo) justo antes de enviar, en vez de perderla silenciosamente.
const ResiduosSelector = forwardRef(function ResiduosSelector({ catalogo, value, onChange, onPendingChange }, ref) {
  const [tipo, setTipo] = useState("");
  const [cantidad, setCantidad] = useState("");
  const [avisoRecorte, setAvisoRecorte] = useState("");

  const info = catalogo.find((c) => c.label === tipo);
  const hasPending = Boolean(tipo && cantidad && Number(cantidad) > 0);

  const handleTipoChange = (v) => {
    setTipo(v);
    setAvisoRecorte("");
    onPendingChange?.(Boolean(v && cantidad && Number(cantidad) > 0));
  };

  const handleCantidadChange = (raw) => {
    let v = sanitizeDecimal(raw);
    const max = info?.max_cantidad;
    if (max && v && Number(v) > max) {
      v = String(max);
      setAvisoRecorte(`Se ha ajustado a la cantidad máxima admitida: ${max} ${info.unidad}`);
    } else {
      setAvisoRecorte("");
    }
    setCantidad(v);
    onPendingChange?.(Boolean(tipo && v && Number(v) > 0));
  };

  const addResiduo = () => {
    if (!tipo || !cantidad || Number(cantidad) <= 0) return false;
    onChange([...value, { tipo, cantidad: Number(cantidad) }]);
    setTipo("");
    setCantidad("");
    setAvisoRecorte("");
    onPendingChange?.(false);
    return true;
  };

  useImperativeHandle(ref, () => ({
    // Devuelve la entrada pendiente (ya recortada al máximo mientras se escribía)
    // sin disparar onChange, para que el padre la incorpore al payload justo antes
    // de enviar sin depender del timing de la actualización de estado de React.
    getPendingEntry: () => (hasPending ? { tipo, cantidad: Number(cantidad) } : null),
  }));

  const removeResiduo = (idx) => {
    onChange(value.filter((_, i) => i !== idx));
    setAvisoRecorte("");
  };

  return (
    <div data-testid="residuos-selector">
      <div className="grid sm:grid-cols-[1fr_140px_auto] gap-2 items-end mb-1">
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">Tipo de residuo</Label>
          <Select value={tipo} onValueChange={handleTipoChange}>
            <SelectTrigger data-testid="residuo-tipo-select">
              <SelectValue placeholder="Selecciona un residuo" />
            </SelectTrigger>
            <SelectContent className="max-h-64">
              {catalogo.map((c) => (
                <SelectItem key={c.label} value={c.label}>
                  {c.label} {c.peligroso ? "⚠" : ""} (máx. {c.max_cantidad} {c.unidad})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-gray-500 mb-1 block">
            Cantidad {info ? `(${info.unidad}, máx. ${info.max_cantidad})` : ""}
          </Label>
          <Input
            type="text"
            inputMode="decimal"
            value={cantidad}
            onChange={(e) => handleCantidadChange(e.target.value)}
            data-testid="residuo-cantidad-input"
          />
        </div>
        <Button type="button" onClick={addResiduo} className="bg-municipal hover:bg-municipal-dark text-white" data-testid="residuo-add-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Añadir
        </Button>
      </div>
      {avisoRecorte && <p className="text-xs text-amber-600 mb-2">{avisoRecorte}</p>}

      {value.length > 0 ? (
        <ul className="space-y-2 mt-2">
          {value.map((item, idx) => {
            const itemInfo = catalogo.find((c) => c.label === item.tipo);
            return (
              <li
                key={idx}
                className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                data-testid={`residuo-item-${idx}`}
              >
                <span className="flex items-center gap-2">
                  {itemInfo?.peligroso && <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                  {item.tipo}
                  <span className="text-gray-400">· {item.cantidad} {itemInfo?.unidad}</span>
                </span>
                <button type="button" onClick={() => removeResiduo(idx)} data-testid={`residuo-remove-${idx}`}>
                  <Trash2 className="w-4 h-4 text-gray-400 hover:text-red-500" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : !hasPending ? (
        <p className="text-xs text-gray-400 mt-2" data-testid="residuos-vacio-aviso">
          Selecciona un residuo, indica la cantidad y pulsa "Añadir". Debes añadir al menos uno para poder registrar la visita.
        </p>
      ) : null}
    </div>
  );
});

export default ResiduosSelector;
